/**
 * Custom Product Options Engine v2.0 — Production
 * Renders dynamic product options from JSON metafield config.
 * Handles: checkbox_group, radio_group, dropdown, text, number_slider, dual_slider
 * Features: cumulative pricing, visibility logic, children, native form properties
 *
 * Changelog v2.0:
 * - All types wrapped in uniform accordion (incl dropdown)
 * - Dropdown: CSS :has() trick for selected value display (def theme pattern)
 * - Checkbox: native theme markup (adjacent sibling + icon-check SVG)
 * - Accordion: isProcessing debounce (def theme pattern)
 * - Price summary: "Total" + "Tax included." with theme colors
 * - Slider thumb: 32px flat (quantity button parity)
 * - Form properties: native hidden inputs, no submit-time injection
 * - Header: label only, no selected value display
 */
(function() {
  'use strict';

  /* ===== UTILS ===== */
  const $ = (s, p) => (p || document).querySelector(s);
  const $$ = (s, p) => [...(p || document).querySelectorAll(s)];

  // i18n — Shopify html lang="en", "ru", "zh-CN" vb.
  const CPO_LOCALE = (document.documentElement.lang || 'en').split('-')[0];
  function getLabel(obj) { return obj['label_' + CPO_LOCALE] || obj.label || ''; }
  function getField(obj, field) { return obj[field + '_' + CPO_LOCALE] || obj[field] || ''; }

  function formatMoney(cents, fmt) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const val = (parseInt(cents, 10) / 100).toFixed(2);
    const amount = val.replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
    const amountNoDecimals = parseInt(val, 10).toLocaleString();
    const amountWithComma = amount.replace('.', ',');
    if (!fmt) return '$' + amount;
    return fmt
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amountWithComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, amountNoDecimals.replace(',', '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  function uid() { return 'cpo-' + Math.random().toString(36).substr(2, 9); }

  /* Icon-check SVG — birebir def theme icons.liquid "check" */
  const ICON_CHECK_SVG = '<svg class="icon icon-check" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.7535 3.17621C12.0531 3.43565 12.0839 3.88688 11.8224 4.18405L4.27954 12.7555C4.14281 12.9109 3.94505 13 3.73715 13C3.52924 13 3.33149 12.9109 3.19476 12.7555L0.177621 9.32695C-0.083892 9.02972 -0.053057 8.57848 0.246501 8.31905C0.546049 8.05962 1.00089 8.09019 1.2624 8.38743L3.73715 11.1996L10.7376 3.24455C10.9991 2.94736 11.4539 2.91677 11.7535 3.17621Z" fill="currentColor"/><path fill-rule="evenodd" clip-rule="evenodd" d="M15.7784 3.19705C16.064 3.46912 16.075 3.92124 15.8029 4.20691L7.63944 12.7783C7.49516 12.9299 7.29173 13.0104 7.08287 12.9989C6.87402 12.9874 6.68068 12.8849 6.55392 12.7184L6.14602 12.1827C5.90707 11.8689 5.96773 11.4207 6.28154 11.1818C6.55116 10.9764 6.91992 10.9922 7.17001 11.1998L14.7685 3.22167C15.0406 2.93601 15.4926 2.92498 15.7784 3.19705Z" fill="currentColor"/></svg>';

  /* Arrow icon for accordion */
  const ARROW_SVG = '<svg class="cpo-accordion-head__icon" width="16" height="16" viewBox="0 0 16 16" fill="none"><polyline points="4,6 8,10 12,6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* Dropdown arrow */
  const DD_ARROW_SVG = '<svg class="icon custom-dropdown__trigger-icon" width="16" height="16" viewBox="0 0 16 16"><polyline points="4,6 8,10 12,6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* ===== CONFIG PARSER ===== */
  function parseConfig(raw) {
    try {
      const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!data || !data.options) return null;
      data.options.sort((a, b) => (a.position || 0) - (b.position || 0));
      return data;
    } catch (e) { console.error('[CPO] Config parse error:', e); return null; }
  }

  /* ===== VISIBILITY ENGINE ===== */
  function evaluateCondition(cond, state) {
    if (!cond) return true;
    if (cond.all) return cond.all.every(c => evaluateSingle(c, state));
    if (cond.any) return cond.any.some(c => evaluateSingle(c, state));
    return evaluateSingle(cond, state);
  }

  function evaluateSingle(c, state) {
    const val = state[c.option_id];
    if (val === undefined) return false;
    const vals = c.values || [c.value];
    switch (c.operator) {
      case 'equals': return vals.includes(val);
      case 'not_equals': return !vals.includes(val);
      case 'in': return vals.includes(val);
      case 'not_in': return !vals.includes(val);
      case 'has': return Array.isArray(val) && vals.some(v => val.includes(v));
      case 'not_has': return !Array.isArray(val) || !vals.some(v => val.includes(v));
      default: return true;
    }
  }

  /* ===== PRICE CALCULATOR ===== */
  function resolvePrice(item, state) {
    let pv = item.price_value || 0;
    let ppu = item.price_per_unit || 0;
    if (item.price_rules) {
      for (const rule of item.price_rules) {
        const sv = state[rule.when.option_id];
        const match = Array.isArray(sv) ? sv.includes(rule.when.value) : sv === rule.when.value;
        if (match) {
          if (rule.price_value !== undefined) pv = rule.price_value;
          if (rule.price_per_unit !== undefined) ppu = rule.price_per_unit;
          break;
        }
      }
    }
    return { price_value: pv, price_per_unit: ppu };
  }

  function calculateTotal(config, state, basePriceCents) {
    let fixedTotal = 0;
    let perUnitTotal = 0;
    let percentItems = [];

    function processOption(opt) {
      if (!evaluateCondition(opt.visible_when, state)) return;
      if (opt.type === 'number_slider') {
        const sv = state[opt.id];
        if (sv !== undefined) {
          const resolved = resolvePrice(opt, state);
          perUnitTotal += sv * resolved.price_per_unit * 100;
        }
      } else if (opt.type === 'dual_slider') {
        const sv = state[opt.id];
        if (sv && opt.price_tiers) {
          for (const tier of opt.price_tiers) {
            const oStart = Math.max(sv.start, tier.from);
            const oEnd = Math.min(sv.end, tier.to);
            if (oStart < oEnd) {
              const units = oEnd - oStart;
              const resolved = resolvePrice(tier, state);
              perUnitTotal += units * resolved.price_per_unit * 100;
            }
          }
        }
      } else if (opt.type === 'checkbox_group' && opt.items) {
        const selected = state[opt.id] || [];
        for (const item of opt.items) {
          if (!selected.includes(item.id)) continue;
          if (!evaluateCondition(item.visible_when, state)) continue;
          const resolved = resolvePrice(item, state);
          if (item.price_type === 'percent') percentItems.push(resolved.price_value);
          else if (item.price_type === 'fixed') fixedTotal += resolved.price_value * 100;
          if (item.children) item.children.forEach(c => processOption(c));
        }
      } else if ((opt.type === 'radio_group' || opt.type === 'dropdown') && opt.items) {
        const sv = state[opt.id];
        const sel = opt.items.find(i => i.id === sv);
        if (sel) {
          if (!evaluateCondition(sel.visible_when, state)) return;
          const resolved = resolvePrice(sel, state);
          if (sel.price_type === 'percent') percentItems.push(resolved.price_value);
          else if (sel.price_type === 'fixed') fixedTotal += resolved.price_value * 100;
          if (sel.children) sel.children.forEach(c => processOption(c));
        }
      }
    }

    config.options.forEach(processOption);
    const subtotal = basePriceCents + fixedTotal + perUnitTotal;
    let percentTotal = 0;
    percentItems.forEach(p => { percentTotal += Math.round(subtotal * p / 100); });
    return {
      base: basePriceCents,
      fixed: fixedTotal,
      perUnit: perUnitTotal,
      subtotal: subtotal,
      percent: percentTotal,
      total: subtotal + percentTotal,
      addonTotal: fixedTotal + perUnitTotal + percentTotal
    };
  }

  /* ===== UI RENDERER ===== */
  const CPO_I18N_REQUIRED = (document.querySelector('[data-i18n-required]') || {}).dataset?.i18nRequired || 'This field is required';

  /**
   * renderSection — Her option tipi accordion'a sarılır.
   * @param {Object} opt - Option config
   * @param {string} productId
   * @param {boolean} isChild - true ise accordion wrapper yok
   */
  function renderSection(opt, productId, isChild) {
    opt._productId = productId;
    const wrap = document.createElement('div');
    wrap.className = 'cpo-section product-custom-field';
    wrap.id = 'cpo-opt-' + opt.id;
    wrap.dataset.optionId = opt.id;
    if (opt.visible_when) wrap.dataset.visibleWhen = JSON.stringify(opt.visible_when);

    // Hidden property input — form'a doğrudan eklenir (native custom field pattern)
    // Text input kendi name'ini taşır, geri kalan tipler hidden input kullanır
    let propertyInput = '';
    if (opt.label && opt.type !== 'text') {
      propertyInput = '<input type="hidden" class="js-cpo-property" name="properties[' + opt.label + ']" data-option-id="' + opt.id + '" value="">';
    }

    // Children (isChild=true) → accordion wrapper yok, düz render
    if (isChild) {
      let inner = '';
      // description kaldırıldı — kalabalık yapıyordu
      inner += renderOptionContent(opt, productId);
      if (opt.required) inner += '<span class="product-custom-field__error error" id="cpo-err-' + opt.id + '" tabindex="-1" role="alert">' + CPO_I18N_REQUIRED + '</span>';
      inner += propertyInput;
      wrap.innerHTML = inner;
      return wrap;
    }

    // Accordion wrapper — TÜM tipler (dropdown dahil)
    let inner = '<div class="cpo-accordion-item cpo-accordion-item--open" data-cpo-accordion>';
    inner += '<button type="button" class="cpo-accordion-head" data-cpo-accordion-trigger>';
    inner += '<span class="cpo-accordion-head__label">' + getLabel(opt) + '</span>';
    inner += ARROW_SVG;
    inner += '</button>';
    inner += '<div class="cpo-accordion-body" data-cpo-accordion-body style="height:auto;">';
    inner += '<div class="cpo-accordion-inner">';
    // description kaldırıldı — kalabalık yapıyordu
    inner += renderOptionContent(opt, productId);
    if (opt.required) inner += '<span class="product-custom-field__error error" id="cpo-err-' + opt.id + '" tabindex="-1" role="alert">' + CPO_I18N_REQUIRED + '</span>';
    inner += '</div></div></div>';
    inner += propertyInput;
    wrap.innerHTML = inner;
    return wrap;
  }

  /**
   * renderOptionContent — Tip bazlı content render (accordion dışında)
   */
  function renderOptionContent(opt, productId) {
    switch (opt.type) {
      case 'checkbox_group': return renderCheckboxGroup(opt, productId);
      case 'radio_group': return renderRadioGroup(opt, productId);
      case 'dropdown': return renderDropdown(opt, productId);
      case 'text': return renderText(opt, productId);
      case 'number_slider': return renderSlider(opt, productId);
      case 'dual_slider': return renderDualSlider(opt, productId);
      default: return '';
    }
  }

  function priceBadge(item) {
    if (!item.price_type || item.price_type === 'none') return '';
    let text = '';
    if (item.price_type === 'fixed' && item.price_value) text = '+' + formatMoney(item.price_value * 100);
    else if (item.price_type === 'percent' && item.price_value) text = '+' + item.price_value + '%';
    else if (item.price_type === 'per_unit' && item.price_per_unit) text = formatMoney(item.price_per_unit * 100) + '/unit';
    if (!text) return '';
    return '<span class="cpo-price-badge" data-item-id="' + item.id + '">' + text + '</span>';
  }

  function renderChildren(children, pId) {
    if (!children || !children.length) return '';
    let h = '<div class="cpo-children" data-children>';
    children.forEach(child => {
      h += renderSection(child, pId, true).outerHTML;
    });
    h += '</div>';
    return h;
  }

  /**
   * Checkbox group — native tema markup
   * Input ve label kardeş (adjacent sibling) → CSS + selector çalışır
   */
  function renderCheckboxGroup(opt) {
    let h = '<div class="cpo-checkbox-group" data-group-id="' + opt.id + '">';
    (opt.items || []).forEach(item => {
      const id = uid();
      const checked = item.default_checked ? ' checked' : '';
      let vw = item.visible_when ? ' data-visible-when=\'' + JSON.stringify(item.visible_when) + '\'' : '';
      h += '<div class="cpo-checkbox-item" data-item-id="' + item.id + '"' + vw + '>';
      h += '<div class="cpo-checkbox-item__row">';
      // Input DIŞARDA (kardeş) — tema CSS selector'ü: .checkbox:checked + .checkbox-label
      h += '<input id="' + id + '" class="checkbox visually-hidden js-cpo-checkbox" type="checkbox" data-option-id="' + opt.id + '" data-item-id="' + item.id + '"' + checked + '>';
      h += '<label for="' + id + '" class="checkbox-label">';
      h += '<span class="checkbox-label__box">' + ICON_CHECK_SVG + '</span>';
      h += getLabel(item) + priceBadge(item);
      h += '</label>';
      h += '</div>';
      h += renderChildren(item.children, opt._productId);
      h += '</div>';
    });
    h += '</div>';
    return h;
  }

  function renderRadioGroup(opt) {
    const name = 'cpo-radio-' + opt.id;
    let h = '<div class="cpo-radio-group" data-group-id="' + opt.id + '">';
    (opt.items || []).forEach(item => {
      const id = uid();
      const checked = item.id === opt.default_value ? ' checked' : '';
      let vw = item.visible_when ? ' data-visible-when=\'' + JSON.stringify(item.visible_when) + '\'' : '';
      h += '<div class="cpo-radio-item" data-item-id="' + item.id + '"' + vw + '>';
      h += '<div class="radio-option">';
      h += '<input id="' + id + '" type="radio" class="visually-hidden radio-input js-cpo-radio" name="' + name + '" value="' + item.id + '" data-option-id="' + opt.id + '" data-item-id="' + item.id + '"' + checked + '>';
      h += '<label for="' + id + '" class="radio-option__label">' + getLabel(item) + priceBadge(item) + '</label>';
      h += '</div>';
      h += renderChildren(item.children, opt._productId);
      h += '</div>';
    });
    h += '</div>';
    return h;
  }

  /**
   * Dropdown — CSS :has() trick (birebir def theme)
   * Her item için ayrı trigger-content span + inline <style>
   */
  function renderDropdown(opt) {
    const items = opt.items || [];
    const defItem = items.find(i => i.id === opt.default_value) || items[0];
    const ddContainerId = uid();

    // Inline <style> oluştur — her radio için :has(:checked) kuralı
    let styleBlock = '<style>';
    const triggerIds = [];
    items.forEach(item => {
      const radioId = uid();
      const triggerId = uid();
      item._radioId = radioId;
      item._triggerId = triggerId;
      triggerIds.push({ radioId, triggerId });
      styleBlock += '#' + ddContainerId + ':has(#' + radioId + ':checked) #' + triggerId + '{display:flex;}';
    });
    styleBlock += '</style>';

    let h = '<div class="cpo-dropdown">';
    h += '<div class="custom-dropdown js-cpo-dropdown" id="' + ddContainerId + '" data-group-id="' + opt.id + '">';
    h += styleBlock;

    // Trigger button — TÜM trigger-content span'ları içinde
    h += '<button class="custom-dropdown__trigger js-cpo-dd-trigger" type="button">';
    items.forEach(item => {
      h += '<span class="custom-dropdown__trigger-content" id="' + item._triggerId + '">' + getLabel(item) + '</span>';
    });
    h += DD_ARROW_SVG;
    h += '</button>';

    // Options panel
    h += '<div class="custom-dropdown__options">';
    items.forEach(item => {
      const checked = item.id === (opt.default_value || (items[0] && items[0].id)) ? ' checked' : '';
      let vw = item.visible_when ? ' data-visible-when=\'' + JSON.stringify(item.visible_when) + '\'' : '';
      h += '<label class="custom-dropdown__option" for="' + item._radioId + '"' + vw + '>';
      h += '<input id="' + item._radioId + '" type="radio" class="visually-hidden radio-input js-cpo-dd-radio" name="cpo-dd-' + opt.id + '" value="' + item.id + '" data-option-id="' + opt.id + '" data-item-id="' + item.id + '" data-label="' + getLabel(item) + '"' + checked + '>';
      h += getLabel(item) + priceBadge(item);
      h += '</label>';
    });
    h += '</div></div>';

    // Children — dropdown panelinin altında, seçili item'a göre gösterilir
    items.forEach(item => {
      if (item.children && item.children.length) {
        h += '<div class="cpo-dropdown-item" data-item-id="' + item.id + '">';
        h += renderChildren(item.children, opt._productId);
        h += '</div>';
      }
    });

    h += '</div>';
    return h;
  }

  function renderText(opt) {
    const id = uid();
    // Text input doğrudan name="properties[Label]" taşır
    let h = '<div class="cpo-text-field">';
    h += '<input id="' + id + '" class="product-custom-field__input input js-cpo-text" type="text" data-option-id="' + opt.id + '"';
    if (opt.label) h += ' name="properties[' + opt.label + ']"';
    h += ' placeholder="' + getField(opt, 'placeholder') + '"';
    if (opt.max_length) h += ' maxlength="' + opt.max_length + '"';
    h += '>';
    h += '</div>';
    return h;
  }

  function renderSlider(opt) {
    const id = uid();
    const min = opt.min || 0, max = opt.max || 100, step = opt.step || 1;
    const def = opt.default_value || min;
    const suffix = getField(opt, 'suffix');
    const fill = ((def - min) / (max - min)) * 100;
    let h = '<div class="cpo-slider" data-group-id="' + opt.id + '">';
    h += '<div class="cpo-slider__header"><span class="cpo-slider__label">' + getLabel(opt) + '</span>';
    h += '<span class="cpo-slider__value" id="cpo-sv-' + opt.id + '">' + def + suffix + '</span></div>';
    h += '<div class="cpo-slider__track-wrapper">';
    h += '<input id="' + id + '" type="range" class="cpo-slider__input js-cpo-slider" min="' + min + '" max="' + max + '" step="' + step + '" value="' + def + '" data-option-id="' + opt.id + '" data-suffix="' + suffix + '" style="--slider-fill:' + fill + '%">';
    h += '</div>';
    h += '<div class="cpo-slider__ticks"><span>' + min + suffix + '</span><span>' + max + suffix + '</span></div>';
    h += '</div>';
    return h;
  }

  function renderDualSlider(opt) {
    const min = opt.min || 0, max = opt.max || 100, step = opt.step || 1;
    const defStart = opt.default_start !== undefined ? opt.default_start : min;
    const defEnd = opt.default_end !== undefined ? opt.default_end : max;
    const suffix = getField(opt, 'suffix');
    const leftPct = ((defStart - min) / (max - min)) * 100;
    const rightPct = ((max - defEnd) / (max - min)) * 100;
    let h = '<div class="cpo-dual-slider" data-group-id="' + opt.id + '">';
    h += '<div class="cpo-dual-slider__header"><span class="cpo-dual-slider__label">' + getLabel(opt) + '</span>';
    h += '<span class="cpo-dual-slider__value" id="cpo-dsv-' + opt.id + '">' + defStart + suffix + ' \u2014 ' + defEnd + suffix + '</span></div>';
    h += '<div class="cpo-dual-slider__track-wrapper">';
    h += '<div class="cpo-dual-slider__track"></div>';
    h += '<div class="cpo-dual-slider__range" id="cpo-dsr-' + opt.id + '" style="left:' + leftPct + '%;right:' + rightPct + '%"></div>';
    h += '<input type="range" class="cpo-dual-slider__input js-cpo-dual-start" min="' + min + '" max="' + max + '" step="' + step + '" value="' + defStart + '" data-option-id="' + opt.id + '" data-suffix="' + suffix + '">';
    h += '<input type="range" class="cpo-dual-slider__input js-cpo-dual-end" min="' + min + '" max="' + max + '" step="' + step + '" value="' + defEnd + '" data-option-id="' + opt.id + '" data-suffix="' + suffix + '">';
    h += '</div>';
    // Tick'leri price_tiers sınırlarından oluştur
    var ticks = [min];
    if (opt.price_tiers) {
      opt.price_tiers.forEach(function(t) {
        if (ticks.indexOf(t.from) === -1) ticks.push(t.from);
        if (ticks.indexOf(t.to) === -1) ticks.push(t.to);
      });
    }
    if (ticks.indexOf(max) === -1) ticks.push(max);
    ticks.sort(function(a, b) { return a - b; });
    h += '<div class="cpo-dual-slider__ticks">';
    ticks.forEach(function(t) {
      var pct = ((t - min) / (max - min)) * 100;
      h += '<span style="left:' + pct + '%">' + t + suffix + '</span>';
    });
    h += '</div>';
    h += '</div>';
    return h;
  }

  function updateDualSliderRange(optId, container) {
    var sInp = $('.js-cpo-dual-start[data-option-id="' + optId + '"]', container);
    var eInp = $('.js-cpo-dual-end[data-option-id="' + optId + '"]', container);
    var rangeEl = $('#cpo-dsr-' + optId);
    if (!sInp || !eInp || !rangeEl) return;
    var min = parseFloat(sInp.min), max = parseFloat(sInp.max);
    rangeEl.style.left = ((parseFloat(sInp.value) - min) / (max - min)) * 100 + '%';
    rangeEl.style.right = ((max - parseFloat(eInp.value)) / (max - min)) * 100 + '%';
  }

  /* ===== STATE MANAGER ===== */
  function collectState(config, container) {
    const state = {};
    config.options.forEach(opt => collectOptionState(opt, container, state));
    return state;
  }

  function collectOptionState(opt, container, state) {
    switch (opt.type) {
      case 'checkbox_group': {
        const checked = $$('.js-cpo-checkbox[data-option-id="' + opt.id + '"]:checked', container);
        state[opt.id] = checked.map(el => el.dataset.itemId);
        (opt.items || []).forEach(item => {
          if (item.children && state[opt.id].includes(item.id)) {
            item.children.forEach(c => collectOptionState(c, container, state));
          }
        });
        break;
      }
      case 'radio_group': {
        const sel = $('.js-cpo-radio[data-option-id="' + opt.id + '"]:checked', container);
        state[opt.id] = sel ? sel.value : null;
        const selItem = (opt.items || []).find(i => i.id === state[opt.id]);
        if (selItem && selItem.children) selItem.children.forEach(c => collectOptionState(c, container, state));
        break;
      }
      case 'dropdown': {
        const sel = $('.js-cpo-dd-radio[data-option-id="' + opt.id + '"]:checked', container);
        state[opt.id] = sel ? sel.value : null;
        const selDdItem = (opt.items || []).find(i => i.id === state[opt.id]);
        if (selDdItem && selDdItem.children) selDdItem.children.forEach(c => collectOptionState(c, container, state));
        break;
      }
      case 'text': {
        const inp = $('.js-cpo-text[data-option-id="' + opt.id + '"]', container);
        state[opt.id] = inp ? inp.value : '';
        break;
      }
      case 'number_slider': {
        const inp = $('.js-cpo-slider[data-option-id="' + opt.id + '"]', container);
        state[opt.id] = inp ? parseFloat(inp.value) : opt.default_value || opt.min || 0;
        break;
      }
      case 'dual_slider': {
        const startInp = $('.js-cpo-dual-start[data-option-id="' + opt.id + '"]', container);
        const endInp = $('.js-cpo-dual-end[data-option-id="' + opt.id + '"]', container);
        state[opt.id] = {
          start: startInp ? parseFloat(startInp.value) : (opt.default_start || opt.min || 0),
          end: endInp ? parseFloat(endInp.value) : (opt.default_end || opt.max || 100)
        };
        break;
      }
    }
  }

  /* ===== CHILDREN RESET ===== */
  /**
   * resetChildrenInputs — Parent deselect olduğunda children'ın
   * tüm input'larını sıfırlar (recursive, iç içe çalışır).
   */
  function resetChildrenInputs(childrenEl) {
    // Checkboxlar → uncheck
    $$('.js-cpo-checkbox', childrenEl).forEach(cb => { cb.checked = false; });
    // Radiolar → uncheck
    $$('.js-cpo-radio', childrenEl).forEach(r => { r.checked = false; });
    // Dropdown radioları → uncheck
    $$('.js-cpo-dd-radio', childrenEl).forEach(r => { r.checked = false; });
    // Text input → temizle + enable
    $$('.js-cpo-text', childrenEl).forEach(t => { t.value = ''; t.disabled = false; });
    // Slider → min değere resetle
    $$('.js-cpo-slider', childrenEl).forEach(s => {
      var minVal = parseFloat(s.min) || 0;
      s.value = minVal;
      s.style.setProperty('--slider-fill', '0%');
      var valEl = $('#cpo-sv-' + s.dataset.optionId);
      if (valEl) valEl.textContent = minVal + (s.dataset.suffix || '');
    });
    // Dual slider → her iki handle'ı default'a resetle
    $$('.js-cpo-dual-start', childrenEl).forEach(function(s) {
      s.value = s.min;
    });
    $$('.js-cpo-dual-end', childrenEl).forEach(function(s) {
      s.value = s.max;
    });
    $$('.cpo-dual-slider', childrenEl).forEach(function(ds) {
      var optId = ds.dataset.groupId;
      var rangeEl = $('#cpo-dsr-' + optId);
      if (rangeEl) { rangeEl.style.left = '0%'; rangeEl.style.right = '0%'; }
      var valEl = $('#cpo-dsv-' + optId);
      var startInp = ds.querySelector('.js-cpo-dual-start');
      var endInp = ds.querySelector('.js-cpo-dual-end');
      if (valEl && startInp && endInp) {
        valEl.textContent = startInp.value + (startInp.dataset.suffix || '') + ' \u2014 ' + endInp.value + (endInp.dataset.suffix || '');
      }
    });
    // Hidden property input → temizle + disable
    $$('.js-cpo-property', childrenEl).forEach(p => { p.value = ''; p.disabled = true; });
    // İç içe children'ları da kapat + resetle (recursive)
    $$('.cpo-children', childrenEl).forEach(nested => {
      nested.classList.remove('cpo-children--visible');
      resetChildrenInputs(nested);
    });
  }

  /* ===== UPDATE CYCLE ===== */
  function update(instance) {
    const { config, container, productId, basePriceCents, moneyFmt } = instance;
    const state = collectState(config, container);
    instance.state = state;

    // Visibility — section gizlenince input'ları da sıfırla
    $$('.cpo-section[data-visible-when]', container).forEach(el => {
      const cond = JSON.parse(el.dataset.visibleWhen);
      const shouldHide = !evaluateCondition(cond, state);
      const wasHidden = el.classList.contains('cpo-section--hidden');
      el.classList.toggle('cpo-section--hidden', shouldHide);
      // Yeni gizlenen section → tüm input'larını sıfırla
      if (shouldHide && !wasHidden) {
        resetChildrenInputs(el);
      }
      // Yeniden görünen section → dropdown/radio default'larını geri koy
      if (!shouldHide && wasHidden) {
        $$('.js-cpo-dropdown', el).forEach(dd => {
          var radios = $$('.js-cpo-dd-radio', dd);
          if (radios.length && !radios.some(r => r.checked)) {
            radios[0].checked = true;
          }
        });
        $$('.cpo-radio-group', el).forEach(rg => {
          var radios = $$('.js-cpo-radio', rg);
          if (radios.length && !radios.some(r => r.checked)) {
            radios[0].checked = true;
          }
        });
      }
    });
    $$('[data-visible-when]', container).forEach(el => {
      if (el.classList.contains('cpo-section')) return;
      const cond = JSON.parse(el.dataset.visibleWhen);
      el.style.display = evaluateCondition(cond, state) ? '' : 'none';
    });

    // Children visibility + reset
    $$('.cpo-children', container).forEach(ch => {
      const parent = ch.closest('.cpo-checkbox-item, .cpo-radio-item, .cpo-dropdown-item');
      if (!parent) return;
      const itemId = parent.dataset.itemId;
      // Dropdown: input panelin içinde, children dışarıda — section scope'da ara
      const cbx = $('input[data-item-id="' + itemId + '"]', parent)
               || $('input[data-item-id="' + itemId + '"]', parent.closest('.cpo-section'));
      if (!cbx) return;
      const shouldShow = cbx.checked;
      const wasVisible = ch.classList.contains('cpo-children--visible');
      ch.classList.toggle('cpo-children--visible', shouldShow);

      // Parent deselected → recursive reset: tüm children input'ları sıfırla
      if (!shouldShow && wasVisible) {
        resetChildrenInputs(ch);
      }
    });

    // Price
    const calc = calculateTotal(config, state, basePriceCents);
    const summaryEl = $('#cpo-price-summary-' + productId);
    const totalEl = $('#cpo-total-' + productId);
    if (summaryEl && totalEl) {
      summaryEl.style.display = '';
      totalEl.textContent = formatMoney(calc.total, moneyFmt);
    }

    // Update price badges with resolved prices
    updatePriceBadges(config, state, container, moneyFmt);

    // Update form property hidden inputs (native custom field pattern)
    updateFormProperties(config, state, container);
  }

  /**
   * updateFormProperties — Her option'ın hidden property input'unu günceller.
   * Recursive: children option'ları da ziyaret eder.
   * Text input'lar kendi name'lerini taşıdığı için atlanır.
   */
  function updateFormProperties(config, state, container) {
    function processOption(opt) {
      if (opt.type === 'text') {
        // Text input kendi name'ini taşıyor — disabled edilmişse tekrar aç
        var textInp = $('.js-cpo-text[data-option-id="' + opt.id + '"]', container);
        if (textInp) textInp.disabled = false;
        return;
      }
      const propInput = $('.js-cpo-property[data-option-id="' + opt.id + '"]', container);
      if (!propInput) return;

      // Görünür değilse boş bırak (form'a gönderilmesin)
      if (!evaluateCondition(opt.visible_when, state)) {
        propInput.value = '';
        propInput.disabled = true;
        // Children'ları da disable et
        disableChildren(opt, container);
        return;
      }
      propInput.disabled = false;

      let val = '';
      switch (opt.type) {
        case 'checkbox_group': {
          const sel = (state[opt.id] || []);
          val = sel.map(id => {
            const item = (opt.items || []).find(i => i.id === id);
            return item ? getLabel(item) : id;
          }).join(', ');
          // Children recursive — seçili item'ların children'ını işle
          (opt.items || []).forEach(item => {
            if (!item.children) return;
            if (sel.includes(item.id)) {
              item.children.forEach(child => processOption(child));
            } else {
              item.children.forEach(child => disableOptionProp(child, container));
            }
          });
          break;
        }
        case 'radio_group':
        case 'dropdown': {
          const selItem = (opt.items || []).find(i => i.id === state[opt.id]);
          val = selItem ? getLabel(selItem) : '';
          // Children recursive — seçili item'ın children'ını işle, diğerlerini disable et
          (opt.items || []).forEach(item => {
            if (!item.children) return;
            if (item.id === state[opt.id]) {
              item.children.forEach(child => processOption(child));
            } else {
              item.children.forEach(child => disableOptionProp(child, container));
            }
          });
          break;
        }
        case 'number_slider': {
          val = (state[opt.id] !== undefined ? state[opt.id] : '') + getField(opt, 'suffix');
          break;
        }
        case 'dual_slider': {
          const sv = state[opt.id];
          val = sv ? (sv.start + getField(opt, 'suffix') + ' \u2014 ' + sv.end + getField(opt, 'suffix')) : '';
          break;
        }
      }
      propInput.value = val;
    }

    // Tek option'ın property input'unu disable et
    function disableOptionProp(opt, cont) {
      const inp = $('.js-cpo-property[data-option-id="' + opt.id + '"]', cont);
      if (inp) { inp.value = ''; inp.disabled = true; }
      // Text input'ları da disable et (kendi name'leri var)
      if (opt.type === 'text') {
        const textInp = $('.js-cpo-text[data-option-id="' + opt.id + '"]', cont);
        if (textInp) textInp.disabled = true;
      }
      disableChildren(opt, cont);
    }

    // Option'ın tüm item children'larını recursive disable et
    function disableChildren(opt, cont) {
      if (!opt.items) return;
      opt.items.forEach(item => {
        if (!item.children) return;
        item.children.forEach(child => disableOptionProp(child, cont));
      });
    }

    config.options.forEach(processOption);
  }

  function updatePriceBadges(config, state, container, moneyFmt) {
    config.options.forEach(opt => {
      if (!opt.items) return;
      opt.items.forEach(item => {
        if (!item.price_rules || !item.price_rules.length) return;
        const badge = $('[data-item-id="' + item.id + '"].cpo-price-badge', container);
        if (!badge) return;
        const resolved = resolvePrice(item, state);
        if (item.price_type === 'fixed') badge.textContent = '+' + formatMoney(resolved.price_value * 100, moneyFmt);
        else if (item.price_type === 'percent') badge.textContent = '+' + resolved.price_value + '%';
      });
    });
  }

  /* ===== VALIDATION — tema native .product-custom-field__error pattern ===== */
  function validate(instance) {
    const { config, state, container } = instance;
    let valid = true;
    // Tüm hata mesajlarını gizle (tema pattern: display toggle)
    $$('.product-custom-field__error', container).forEach(e => { e.style.display = ''; });

    function validateOpt(opt) {
      if (!opt.required) { /* required değilse atla ama children'a yine bak */ }
      else if (!evaluateCondition(opt.visible_when, state)) { /* gizliyse atla */ }
      else {
        const errEl = $('#cpo-err-' + opt.id, container);
        let isInvalid = false;
        switch (opt.type) {
          case 'checkbox_group': isInvalid = !state[opt.id] || state[opt.id].length === 0; break;
          case 'radio_group':
          case 'dropdown': isInvalid = !state[opt.id]; break;
          case 'text': isInvalid = !state[opt.id] || !state[opt.id].trim(); break;
          case 'number_slider': isInvalid = state[opt.id] === undefined; break;
          case 'dual_slider': { const sv = state[opt.id]; isInvalid = !sv || sv.start === undefined || sv.end === undefined; break; }
        }
        if (isInvalid) {
          valid = false;
          if (errEl) errEl.style.display = 'block';
        }
      }
      // Seçili item'ların children'larını recursive validate et
      if (opt.type === 'checkbox_group' && opt.items) {
        var selected = state[opt.id] || [];
        opt.items.forEach(function(item) {
          if (selected.includes(item.id) && item.children) {
            item.children.forEach(validateOpt);
          }
        });
      } else if ((opt.type === 'radio_group' || opt.type === 'dropdown') && opt.items) {
        var sel = opt.items.find(function(i) { return i.id === state[opt.id]; });
        if (sel && sel.children) {
          sel.children.forEach(validateOpt);
        }
      }
    }

    config.options.forEach(validateOpt);
    return valid;
  }

  /* ===== FORM INTERCEPTOR ===== */
  function interceptForm(instance) {
    const { container, config } = instance;
    const form = container.closest('form[action*="/cart/add"]');
    if (!form) return;

    form.addEventListener('submit', function(e) {
      update(instance);
      if (!validate(instance)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const firstErr = $('.product-custom-field__error[style*="display"]', container);
        if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }

      // Globo addon pricing property (hesaplama gerektirir, submit anında inject)
      const calc = calculateTotal(config, instance.state, instance.basePriceCents);
      if (calc.addonTotal > 0) {
        const variantId = container.dataset.variantId;
        const gpoData = JSON.stringify([{
          i: parseInt(variantId, 10), q: 1,
          p: String((calc.addonTotal / 100).toFixed(2)), t: 'price'
        }]);

        // 1. Globo'nun gizli checkbox'ını check et (Cart Transform tetikleyici)
        var globoCb = form.querySelector('input[name="kalimdor"]')
                   || form.querySelector('.gpo-container input[type="checkbox"]');
        if (globoCb && !globoCb.checked) {
          globoCb.checked = true;
          globoCb.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // 2. _has_gpo property — Globo option set ID (checkbox'tan alınır)
        var gpoSetId = globoCb ? (globoCb.dataset.gpoSetId || globoCb.closest('[data-gpo-set-id]')?.dataset.gpoSetId || '1') : '1';
        let hasGpoInput = form.querySelector('input[name="properties[_has_gpo]"]');
        if (!hasGpoInput) {
          hasGpoInput = document.createElement('input');
          hasGpoInput.type = 'hidden';
          hasGpoInput.name = 'properties[_has_gpo]';
          form.appendChild(hasGpoInput);
        }
        hasGpoInput.value = gpoSetId;

        // 3. _gpo_addon_products — hesaplanan fiyat
        let gpoInput = form.querySelector('input[name="properties[_gpo_addon_products]"]');
        if (!gpoInput) {
          gpoInput = document.createElement('input');
          gpoInput.type = 'hidden';
          gpoInput.name = 'properties[_gpo_addon_products]';
          form.appendChild(gpoInput);
        }
        gpoInput.value = gpoData;

        // 4. _options_total — okunabilir toplam
        let totalInput = form.querySelector('input[name="properties[_options_total]"]');
        if (!totalInput) {
          totalInput = document.createElement('input');
          totalInput.type = 'hidden';
          totalInput.name = 'properties[_options_total]';
          form.appendChild(totalInput);
        }
        totalInput.value = formatMoney(calc.addonTotal);
      } else {
        // Addon 0 → Globo property'lerini temizle
        var rmGpo = form.querySelector('input[name="properties[_gpo_addon_products]"]');
        var rmHas = form.querySelector('input[name="properties[_has_gpo]"]');
        var rmTot = form.querySelector('input[name="properties[_options_total]"]');
        if (rmGpo) rmGpo.remove();
        if (rmHas) rmHas.remove();
        if (rmTot) rmTot.remove();
        // Globo checkbox'ı da uncheck et
        var globoCbClean = form.querySelector('input[name="kalimdor"]')
                        || form.querySelector('.gpo-container input[type="checkbox"]');
        if (globoCbClean && globoCbClean.checked) globoCbClean.checked = false;
      }
    });
  }

  /* ===== DROPDOWN TOGGLE ===== */
  function initDropdowns(container) {
    document.addEventListener('click', function(e) {
      const trigger = e.target.closest('.js-cpo-dd-trigger');
      if (trigger) {
        e.preventDefault();
        const dd = trigger.closest('.js-cpo-dropdown');
        if (dd) {
          // Diğer dropdown'ları kapat
          $$('.js-cpo-dropdown.is-active', container).forEach(d => { if (d !== dd) d.classList.remove('is-active'); });
          dd.classList.toggle('is-active');
        }
        return;
      }
      // Dışına tıklama → tüm dropdown'ları kapat
      if (!e.target.closest('.js-cpo-dropdown')) {
        $$('.js-cpo-dropdown.is-active', container).forEach(d => d.classList.remove('is-active'));
      }
    });

    // Escape ile kapat
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        $$('.js-cpo-dropdown.is-active', container).forEach(d => d.classList.remove('is-active'));
      }
    });
  }

  /* ===== ACCORDION ENGINE (def theme isProcessing pattern) ===== */
  function initAccordion(container) {
    let isProcessing = false;
    const ANIMATION_DELAY = 500;

    container.addEventListener('click', function(e) {
      const trigger = e.target.closest('[data-cpo-accordion-trigger]');
      if (!trigger) return;
      const item = trigger.closest('[data-cpo-accordion]');
      if (!item) return;

      // Debounce — def theme birebir
      if (isProcessing) return;

      const body = item.querySelector('[data-cpo-accordion-body]');
      const inner = item.querySelector('.cpo-accordion-inner');
      if (!body || !inner) return;

      const isOpen = item.classList.contains('cpo-accordion-item--open');
      isProcessing = true;

      if (isOpen) {
        // Collapse: height → innerHeight → 0
        body.style.height = inner.offsetHeight + 'px';
        requestAnimationFrame(() => {
          body.style.height = '0px';
          isProcessing = false;
        });
        item.classList.remove('cpo-accordion-item--open');
      } else {
        // Expand: height → innerHeight → auto
        body.style.height = '0px';
        requestAnimationFrame(() => {
          body.style.height = inner.offsetHeight + 'px';
        });
        item.classList.add('cpo-accordion-item--open');
        setTimeout(() => {
          body.style.height = 'auto';
          isProcessing = false;
        }, ANIMATION_DELAY);
      }
    });
  }

  /* ===== INIT ===== */
  function init() {
    const containers = $$('.cpo-container');
    containers.forEach(container => {
      const productId = container.dataset.productId;
      const configEl = $('#cpo-config-' + productId);
      const moneyFmtEl = $('#cpo-money-format');
      if (!configEl) return;

      const rawConfig = configEl.textContent.trim();
      let parsed;
      try { parsed = JSON.parse(rawConfig); } catch(e) { return; }
      const config = parseConfig(typeof parsed === 'string' ? JSON.parse(parsed) : parsed);
      if (!config) return;

      const basePriceCents = parseInt(container.dataset.basePrice, 10) || 0;
      const moneyFmt = moneyFmtEl ? JSON.parse(moneyFmtEl.textContent.trim()) : null;
      const sectionsEl = $('#cpo-sections-' + productId);

      // Render all options
      config.options.forEach(opt => {
        sectionsEl.appendChild(renderSection(opt, productId, false));
      });

      const instance = { config, container, productId, basePriceCents, moneyFmt, state: {} };

      // Event delegation — change
      container.addEventListener('change', function(e) {
        // Dropdown: sadece kapat (seçili değer CSS :has() ile gösteriliyor)
        if (e.target.classList.contains('js-cpo-dd-radio')) {
          const dd = e.target.closest('.js-cpo-dropdown');
          if (dd) dd.classList.remove('is-active');
        }
        // Slider value display
        if (e.target.classList.contains('js-cpo-slider')) {
          const suffix = e.target.dataset.suffix || '';
          const valEl = $('#cpo-sv-' + e.target.dataset.optionId);
          if (valEl) valEl.textContent = e.target.value + suffix;
          const pct = ((e.target.value - e.target.min) / (e.target.max - e.target.min)) * 100;
          e.target.style.setProperty('--slider-fill', pct + '%');
        }
        // Dual slider constraint + display
        if (e.target.classList.contains('js-cpo-dual-start') || e.target.classList.contains('js-cpo-dual-end')) {
          const optId = e.target.dataset.optionId;
          const sInp = $('.js-cpo-dual-start[data-option-id="' + optId + '"]', container);
          const eInp = $('.js-cpo-dual-end[data-option-id="' + optId + '"]', container);
          if (e.target.classList.contains('js-cpo-dual-start') && parseFloat(sInp.value) > parseFloat(eInp.value)) sInp.value = eInp.value;
          if (e.target.classList.contains('js-cpo-dual-end') && parseFloat(eInp.value) < parseFloat(sInp.value)) eInp.value = sInp.value;
          const suffix = e.target.dataset.suffix || '';
          const valEl = $('#cpo-dsv-' + optId);
          if (valEl) valEl.textContent = sInp.value + suffix + ' \u2014 ' + eInp.value + suffix;
          updateDualSliderRange(optId, container);
        }
        update(instance);
      });

      // Slider live input
      container.addEventListener('input', function(e) {
        if (e.target.classList.contains('js-cpo-slider')) {
          const suffix = e.target.dataset.suffix || '';
          const valEl = $('#cpo-sv-' + e.target.dataset.optionId);
          if (valEl) valEl.textContent = e.target.value + suffix;
          const pct = ((e.target.value - e.target.min) / (e.target.max - e.target.min)) * 100;
          e.target.style.setProperty('--slider-fill', pct + '%');
          update(instance);
        }
        // Dual slider live input
        if (e.target.classList.contains('js-cpo-dual-start') || e.target.classList.contains('js-cpo-dual-end')) {
          const optId = e.target.dataset.optionId;
          const sInp = $('.js-cpo-dual-start[data-option-id="' + optId + '"]', container);
          const eInp = $('.js-cpo-dual-end[data-option-id="' + optId + '"]', container);
          if (e.target.classList.contains('js-cpo-dual-start') && parseFloat(sInp.value) > parseFloat(eInp.value)) sInp.value = eInp.value;
          if (e.target.classList.contains('js-cpo-dual-end') && parseFloat(eInp.value) < parseFloat(sInp.value)) eInp.value = sInp.value;
          const suffix = e.target.dataset.suffix || '';
          const valEl = $('#cpo-dsv-' + optId);
          if (valEl) valEl.textContent = sInp.value + suffix + ' \u2014 ' + eInp.value + suffix;
          updateDualSliderRange(optId, container);
          update(instance);
        }
      });

      // Text input live update for properties
      container.addEventListener('input', function(e) {
        if (e.target.classList.contains('js-cpo-text')) {
          update(instance);
        }
      });

      initDropdowns(container);
      initAccordion(container);
      interceptForm(instance);

      // Initial state
      update(instance);
    });
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
